import { useSyncExternalStore } from 'react';
import type { Pin } from 'utils/pins';
import type { RecentlyViewedRecord } from 'utils/recentlyViewed';

/**
 * The route a tour started from, so a reload that ends the tour lands back there: the app's only storage write
 * during a tour. PostHog also persists its `tour_mode` super property until the tour ends.
 */
export const TOUR_RETURN_KEY = 'crespect.tour.return';

/** The sample data one tour runs on. */
export type TourSample = {
    /** A read's answer by request path and query; the first pattern that matches wins. */
    responses: ReadonlyArray<readonly [RegExp, unknown]>;
    pins: Pin[];
    recentlyViewed: RecentlyViewedRecord[];
};

/** The sample case the record steps open. */
export const TOUR_MATTER_ID = 900001;

/** What the sample data takes from the real user, so it offers only what they could do. */
export type TourSampleContext = {
    canEditCases: boolean;
};

/** How tour mode answers a request it keeps from the server. */
export type TourAnswer = { data: unknown } | { error: 404 | 'refused' };

// The current user still comes from the server, so the design and the preferences stay the user's own.
const PASS_THROUGH = /^\/v1\/users\/current(\?|$)/;

// A widget's figure before the widget is stored: asked by POST, its definition being the body, and it stores nothing.
const POSTED_READ = /^\/v1\/home-widgets\/figure(\?|$)/;

let sample: TourSample | null = null;
const listeners = new Set<() => void>();

const setSample = (next: TourSample | null) => {
    sample = next;
    listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/** True while the tour runs: requests are answered from sample data, and the app saves nothing but the return key. */
export const isTourMode = (): boolean => sample !== null;

/** The running tour's sample data. */
export const tourSample = (): TourSample | null => sample;

export const useTourMode = (): boolean => useSyncExternalStore(subscribe, isTourMode);

/** Turns tour mode on, on the sample data of the tour that starts. */
export const enterTourMode = (next: TourSample): void => setSample(next);

export const leaveTourMode = (): void => setSample(null);

/**
 * Answers a request while the tour runs: a read from the sample data, or 404 when there is none; anything that
 * writes is refused. A read is a GET, or the one POST that only reads. Undefined lets the request through, which
 * only the current user's own read gets.
 */
export function answerTourRequest(method: string, url: string): TourAnswer | undefined {
    if (!sample) return undefined;
    const verb = method.toUpperCase();
    const { pathname, search } = new URL(url, 'http://tour.invalid');
    const path = `${pathname}${search}`;
    if (verb === 'GET' && PASS_THROUGH.test(path)) return undefined;
    if (verb !== 'GET' && !(verb === 'POST' && POSTED_READ.test(path))) {
        if (import.meta.env.DEV) console.warn(`[tour] refused ${verb} ${path}`);
        return { error: 'refused' };
    }
    const hit = sample.responses.find(([pattern]) => pattern.test(path));
    if (hit) return { data: structuredClone(hit[1]) };
    if (import.meta.env.DEV) console.warn(`[tour] no fixture for ${verb} ${path}`);
    return { error: 404 };
}
